import { concat, IterUtils, reverseArray } from "../../../../../Common/IterableUtils";
import {
    AbsoluteModelPath,
    convertToAbsoluteModelPath,
    createAbsoluteFromTokens,
    createFromMask,
    ModelPath,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import { BindingPath } from "../CoreTypes";
import { IErrorManager } from "../ErrorManager/ErrorManager";
import { isChangeViewModelEvent, ModelEvent } from "../EventManagement/ModelEvents/ModelEvent";
import { IModel } from "../ModelStoring/Model";
import { Predicate, truePredicate } from "../../../../../Common/Funcs";
import { DataDeclarationHelper } from "../DataDeclaration/DataDeclarationHelper";
import { MatchKind } from "../../../../../Common/ModelPath/PathMatcher";
import { InvalidProgramStateError } from "../../../../../Common/Errors";
import { Map2 } from "../../../../../Common/ModelPath/Map/Map2";
import { Set2 } from "../../../../../Common/ModelPath/Set/Set2";
import { isAggregationPath } from "../../../../../Common/ModelPath/ModelPathHelper";
import { IPicklistsProvider } from "../Picklists/IPicklistsProvider";
import { IExternalPropertiesProvider } from "../../Form/IExternalPropertiesProvider";
import { arrayHasLength } from "../../../../../Common/TypingUtils";
import { EachCurrentCollision } from "../../../../../Common/ModelPath/EachCurrentCollision";
import { AbsoluteModelPathSet } from "../../../../../Common/ModelPath/Set/AbsoluteModelPathSet";

import { AggregatedValidationFunction } from "./AggregatedValidationFunction";
import { CalculationTarget, CalculationTargetAttribute } from "./CalculationTarget";
import { EvaluationContext } from "./EvaluationContext";
import { CalculationFunctionExecutionPlan } from "./ExecutionPlan/CalculationFunctionExecutionPlan";
import { ICalculationFunction } from "./ICalculationFunction";
import { LegacyValidationFunctionAdapter } from "./Validations/LegacyValidationFunctionAdapter";
import { ValidationFunction } from "./Validations/ValidationFunction";
import { IIgnoredPathChecker } from "./IIgnoredPathChecker";
import { AddedInstancesInfo } from "./AddedInstancesInfo";
import { CalculationFunctionDependency } from "./CalculationFunctionDependency";

export interface ICalculator2 {
    addCalculationFunction(calculationFunction: ICalculationFunction): void;
    process(model: IModel, eventArg: ModelEvent, acceptFunction?: Predicate<ICalculationFunction>): Promise<void>;
    processSync(model: IModel, eventArg: ModelEvent, acceptFunction?: Predicate<ICalculationFunction>): void;
    processEvents(model: IModel, modelChangeEventInfos: ModelEvent[]): Promise<void>;
    calculateAll(
        model: IModel,
        acceptTarget: Predicate<AbsoluteModelPath>,
        acceptFunction?: Predicate<ICalculationFunction>
    ): Promise<void>;
    calculateFunctions(model: IModel, acceptFunction: Predicate<ICalculationFunction>): Promise<void>;
    recalculateTargets(model: IModel, paths: readonly BindingPath[]): Promise<void>;
    revalidateTargets(model: IModel, paths: AbsoluteModelPath[]): Promise<void>;
    getArgumentsPaths(calcPath: AbsoluteModelPath): AbsoluteModelPath[];
    ensurePrepared(): void;

    getFunctionsByTarget(path: AbsoluteModelPath): ICalculationFunction[];
    clearFunctions(): void;
}

interface IDependencyNode {
    id: number;
    calculationFunction: ICalculationFunction;
    nextNodes: IDependencyNode[];
}

export class Calculator2 implements ICalculator2 {
    private calculationFunctions: ICalculationFunction[] = [];
    private readonly picklistsProvider: IPicklistsProvider;
    private readonly ignoredPathChecker: IIgnoredPathChecker;
    private readonly errorManager: IErrorManager;
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private cachedNextFunctions: Map2<CalculationTarget, ICalculationFunction[]> | undefined;
    private readonly externalPropertiesProvider: IExternalPropertiesProvider;

    public constructor(
        errorManager: IErrorManager,
        dataDeclarationHelper: DataDeclarationHelper,
        ignoredPathChecker: IIgnoredPathChecker,
        picklistsProvider: IPicklistsProvider,
        externalPropertiesProvider: IExternalPropertiesProvider
    ) {
        this.externalPropertiesProvider = externalPropertiesProvider;
        this.picklistsProvider = picklistsProvider;
        this.ignoredPathChecker = ignoredPathChecker;
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.errorManager = errorManager;
    }

    public clearFunctions(): void {
        this.calculationFunctions = [];
        this.dropCache();
    }

    public getFunctionsByTarget(path: AbsoluteModelPath): ICalculationFunction[] {
        return this.calculationFunctions.filter(calculationFunction =>
            calculationFunction
                .getTarget()
                .modelPath.matchFor(path)
                .every(match => match.kind !== MatchKind.DifferentSuffix)
        );
    }

    public addCalculationFunction(calculationFunction: ICalculationFunction): void {
        if (
            calculationFunction instanceof ValidationFunction ||
            calculationFunction instanceof LegacyValidationFunctionAdapter
        ) {
            let aggregatedByTargetFunction = this.calculationFunctions.find(
                (x): x is AggregatedValidationFunction =>
                    x instanceof AggregatedValidationFunction && x.getTarget().isEquals(calculationFunction.getTarget())
            );
            if (aggregatedByTargetFunction == undefined) {
                aggregatedByTargetFunction = new AggregatedValidationFunction(calculationFunction.getTarget());
                this.calculationFunctions.push(aggregatedByTargetFunction);
            }
            aggregatedByTargetFunction.addValidationFunction(calculationFunction);
        } else {
            this.calculationFunctions.push(calculationFunction);
        }
        this.dropCache();
    }

    public async processEvents(model: IModel, modelChangeEventInfos: ModelEvent[]): Promise<void> {
        this.ensurePrepared();
        for (const x of modelChangeEventInfos) {
            await this.processEvent(model, x, () => true);
        }
    }

    public async calculateFunctions(model: IModel, acceptFunction: Predicate<ICalculationFunction>): Promise<void> {
        const orderedCalculationsFunctions = this.getAllOrderedFunctions();

        for (const calculationFunction of orderedCalculationsFunctions) {
            if (acceptFunction(calculationFunction)) {
                const executionPlan = calculationFunction.getFullPlan();
                const context = this.createEvaluationContext(model);
                await executionPlan.execute(context);
            }
        }
    }

    public async calculateAll(
        model: IModel,
        acceptTarget: Predicate<AbsoluteModelPath>,
        acceptFunction?: Predicate<ICalculationFunction>
    ): Promise<void> {
        const orderedCalculationsFunctions = this.getAllOrderedFunctions()
            .filter(acceptFunction ?? truePredicate)
            .filter(calculationFunction => acceptTarget(calculationFunction.getTarget().modelPath.toAbsolute()));

        for (const calculationFunction of orderedCalculationsFunctions) {
            const executionPlan = calculationFunction.getFullPlan();
            const context = this.createEvaluationContext(model);
            await executionPlan.execute(context);
        }
    }

    public async revalidateTargets(model: IModel, targetPaths: AbsoluteModelPath[]): Promise<void> {
        const executionPlans: CalculationFunctionExecutionPlan[] = [];
        for (const targetPath of targetPaths) {
            for (const calculationFunction of this.calculationFunctions) {
                if (calculationFunction.getTarget().targetAttribute !== CalculationTargetAttribute.Validation) {
                    continue;
                }
                if (calculationFunction.getTarget().modelPath.isIncludes(targetPath)) {
                    executionPlans.push(calculationFunction.getPlanForTarget(targetPath));
                    continue;
                }
            }
        }
        for (const executionPlan of executionPlans) {
            const context = this.createEvaluationContext(model);
            await executionPlan.execute(context);
        }
    }

    public async recalculateTargets(model: IModel, targetLegacyPaths: readonly string[]): Promise<void> {
        const targetPaths = Iterator.from(targetLegacyPaths).map(x => createFromMask(x, true, PathTokens.each));
        const executionPlans: CalculationFunctionExecutionPlan[] = [];
        for (const targetPath of targetPaths) {
            for (const calculationFunction of this.calculationFunctions) {
                if (calculationFunction.getTarget().modelPath.isIncludes(targetPath)) {
                    executionPlans.push(calculationFunction.getPlanForTarget(targetPath));
                    continue;
                }
            }
        }
        for (const executionPlan of executionPlans) {
            const context = this.createEvaluationContext(model);
            await executionPlan.execute(context);
        }
    }

    public async processEvent(
        model: IModel,
        modelChangeEventInfo: ModelEvent,
        acceptFunction: Predicate<ICalculationFunction>
    ): Promise<void> {
        const changeModelPath = modelChangeEventInfo.path;
        const orderedCalculationsFunctions = Iterator.from(
            this.getOrderedFunctions(new CalculationTarget(changeModelPath, CalculationTargetAttribute.Value))
        ).filter(acceptFunction);
        const allEventBatches: ModelEvent[][] = [];
        const addedChildren = this.getAddedChildren(modelChangeEventInfo);
        const addedChildrenInfo: AddedInstancesInfo | undefined =
            addedChildren != undefined
                ? { path: modelChangeEventInfo.path, instancesToIterate: addedChildren }
                : undefined;
        for (const calculationFunction of orderedCalculationsFunctions) {
            const allModelValueChangeEvents: Iterable<ModelEvent> = concat(
                [modelChangeEventInfo],
                Iterator.from(allEventBatches)
                    .flatMap(x => x)
                    .filter(isChangeViewModelEvent)
            );
            const executionPlans = this.getExecutionPlan(
                calculationFunction,
                allModelValueChangeEvents,
                calculationFunction.pathIsOutsideDependenciesList(changeModelPath),
                addedChildrenInfo
            );
            if (executionPlans == undefined) {
                continue;
            }

            const eventCollectionContext = model.beginGetEvents();
            const eventsForContext = concat(
                [modelChangeEventInfo],
                Iterator.from(allEventBatches)
                    .flatMap(x => x)
                    .filter(isChangeViewModelEvent)
            );
            const context = this.createEvaluationContext(model, eventsForContext);
            for (const executionPlan of executionPlans) {
                const result = executionPlan.execute(context);
                if (result != undefined) {
                    await result;
                }
            }

            const nextEvents = eventCollectionContext.releaseAndGetEvent();
            allEventBatches.push(nextEvents);
        }
    }

    public processEventSync(
        model: IModel,
        modelChangeEventInfo: ModelEvent,
        acceptFunction: Predicate<ICalculationFunction>
    ): void {
        const changeModelPath = modelChangeEventInfo.path;
        const orderedCalculationsFunctions = Iterator.from(
            this.getOrderedFunctions(new CalculationTarget(changeModelPath, CalculationTargetAttribute.Value))
        ).filter(acceptFunction);
        const allEventBatches: ModelEvent[][] = [];
        const addedChildren = this.getAddedChildren(modelChangeEventInfo);
        const addedChildrenInfo: AddedInstancesInfo | undefined =
            addedChildren != undefined
                ? { path: modelChangeEventInfo.path, instancesToIterate: addedChildren }
                : undefined;
        for (const calculationFunction of orderedCalculationsFunctions) {
            const allModelValueChangeEvents: Iterable<ModelEvent> = concat(
                [modelChangeEventInfo],
                Iterator.from(allEventBatches)
                    .flatMap(x => x)
                    .filter(isChangeViewModelEvent)
            );
            const executionPlans = this.getExecutionPlan(
                calculationFunction,
                allModelValueChangeEvents,
                calculationFunction.pathIsOutsideDependenciesList(changeModelPath),
                addedChildrenInfo,
                true
            );
            if (executionPlans == undefined) {
                continue;
            }

            const eventCollectionContext = model.beginGetEvents();
            const eventsForContext = concat(
                [modelChangeEventInfo],
                Iterator.from(allEventBatches)
                    .flatMap(x => x)
                    .filter(isChangeViewModelEvent)
            );
            const context = this.createEvaluationContext(model, eventsForContext);
            for (const executionPlan of executionPlans) {
                const result = executionPlan.execute(context);
                if (result != undefined) {
                    throw new InvalidProgramStateError();
                }
            }

            const nextEvents = eventCollectionContext.releaseAndGetEvent();
            allEventBatches.push(nextEvents);
        }
    }

    public process(
        model: IModel,
        eventArg: ModelEvent,
        acceptFunction?: Predicate<ICalculationFunction>
    ): Promise<void> {
        return this.processEvent(model, eventArg, acceptFunction ?? (() => true));
    }

    public processSync(model: IModel, eventArg: ModelEvent, acceptFunction?: Predicate<ICalculationFunction>): void {
        this.processEventSync(model, eventArg, acceptFunction ?? (() => true));
    }

    private getAddedChildren(modelChangeEventInfo: ModelEvent): string[] | undefined {
        if (modelChangeEventInfo.name === "children") {
            const value = Array.isArray(modelChangeEventInfo.value) ? modelChangeEventInfo.value : [];
            const oldValue = Array.isArray(modelChangeEventInfo.oldValue) ? modelChangeEventInfo.oldValue : [];
            const oldValueSet = new Set<string>(oldValue);
            const addedChildren: string[] = value.filter((x: string) => !oldValueSet.has(x));
            return addedChildren;
        }
        return undefined;
    }

    private getExecutionPlan(
        calculationFunction: ICalculationFunction,
        allModelValueChangeEvents: Iterable<ModelEvent>,
        changedPathIsOutsideFunctionDependencies?: boolean,
        iterationData?: AddedInstancesInfo,
        sync?: boolean
    ): CalculationFunctionExecutionPlan[] | undefined {
        const targetPaths = new Set2<ModelPath>();
        let targetPathIterationRank = -1;
        for (const modelEvent of allModelValueChangeEvents) {
            const dependencies = calculationFunction.getDependencies();
            for (const dependency of dependencies) {
                let currentTargetPath: undefined | ModelPath;

                if (modelEvent.name === "value") {
                    currentTargetPath = dependency.getTargetByChangedPath(modelEvent.path);
                }
                if (modelEvent.name === "children") {
                    currentTargetPath = dependency.getTargetByChangedChildrenPath(modelEvent.path);
                }

                if (currentTargetPath != undefined) {
                    const currentTargetPathRank = currentTargetPath.getIterationRank();
                    if (currentTargetPathRank === targetPathIterationRank && changedPathIsOutsideFunctionDependencies) {
                        targetPaths.add(currentTargetPath);
                    }

                    if (currentTargetPathRank > targetPathIterationRank) {
                        targetPaths.add(currentTargetPath);
                        targetPathIterationRank = currentTargetPathRank;
                    }
                }
            }

            if (dependencies.length === 0 && modelEvent.name === "children") {
                const tempDependency = new CalculationFunctionDependency(
                    calculationFunction.getTarget().modelPath,
                    createAbsoluteFromTokens(calculationFunction.getTarget().modelPath.getLastListPath())
                );
                const currentTargetPath = tempDependency.getTargetByChangedChildrenPath(modelEvent.path);
                if (currentTargetPath != undefined) {
                    targetPaths.add(currentTargetPath);
                }
            }
        }

        return targetPaths.isEmpty()
            ? undefined
            : Iterator.from(targetPaths.values())
                  .map(x => calculationFunction.getPlanForTarget(x, iterationData, sync))
                  .toArray();
    }

    private dfs(
        calculationTarget: CalculationTarget,
        visited: CalculationTarget[],
        fns: ICalculationFunction[],
        fn: undefined | ICalculationFunction
    ): void {
        if (visited.find(x => x.isEquals(calculationTarget))) {
            return;
        }
        const deps = this.calculationFunctions.filter(x => x.isDependsOn(calculationTarget));
        if (deps.length > 0) {
            for (const dep of deps) {
                this.dfs(dep.getTarget(), visited, fns, dep);
            }
        }
        if (fn != undefined) {
            fns.unshift(fn);
        }
        visited.push(calculationTarget);
    }

    private linkTargetNodeByDependencyToNode(
        targetMap: Map2<CalculationTarget, IDependencyNode>,
        dependencyTarget: CalculationTarget,
        currentNode: IDependencyNode
    ) {
        const targetNode = targetMap.get(dependencyTarget);
        if (targetNode != undefined) {
            targetNode.nextNodes = [
                ...IterUtils.distinctBy([...targetNode.nextNodes, currentNode], x =>
                    x.calculationFunction.getTarget().toString()
                ),
            ];
        }
    }

    private addToGraph(
        dependencyMap: Map2<CalculationTarget, IDependencyNode[]>,
        targetMap: Map2<CalculationTarget, IDependencyNode>,
        calculationFunction: ICalculationFunction,
        functionId: number
    ) {
        const dependentNodes = dependencyMap.get(calculationFunction.getTarget()) ?? [];
        const currentNode = {
            calculationFunction: calculationFunction,
            nextNodes: dependentNodes,
            id: functionId,
        };
        const dependencyTargets = IterUtils.distinctBy(
            calculationFunction
                .getDependencies()
                .map(dep => new CalculationTarget(dep.getDependencyPath(), CalculationTargetAttribute.Value)),
            x => x.toString()
        );
        for (const dependencyTarget of dependencyTargets) {
            dependencyMap.setOrUpdate(
                dependencyTarget,
                __ => [currentNode],
                (_, value) => [...value, currentNode]
            );
            this.linkTargetNodeByDependencyToNode(targetMap, dependencyTarget, currentNode);
        }
        targetMap.set(calculationFunction.getTarget(), currentNode);

        const modelPath = calculationFunction.getTarget().modelPath;
        const modelPathIterationRank = modelPath.getIterationRank(EachCurrentCollision.AreSame);
        const isRequiredToAddDependencyForMultiplicity =
            modelPathIterationRank > 0 &&
            (arrayHasLength(calculationFunction.getDependencies(), 0) ||
                calculationFunction
                    .getDependencies()
                    .some(
                        dep =>
                            dep.getDependencyPath().getIterationRank(EachCurrentCollision.AreSame) !=
                            modelPathIterationRank
                    ));
        if (isRequiredToAddDependencyForMultiplicity) {
            dependencyMap.setOrUpdate(
                new CalculationTarget(
                    createAbsoluteFromTokens(modelPath.getLastListPath()),
                    CalculationTargetAttribute.Value
                ),
                __ => [currentNode],
                (_, value) => [...value, currentNode]
            );
        }
    }

    private buildFunctionsGraphAndGetDependencyMap(
        calculationFunctions: ICalculationFunction[]
    ): Map2<CalculationTarget, IDependencyNode[]> {
        const dependencyMap = new Map2<CalculationTarget, IDependencyNode[]>();
        const targetMap = new Map2<CalculationTarget, IDependencyNode>();
        for (const [index, calculationFunction] of calculationFunctions.entries()) {
            this.addToGraph(dependencyMap, targetMap, calculationFunction, index);
        }
        return dependencyMap;
    }

    private buildCachedOrderedFunctions(
        dependencyMap: Map2<CalculationTarget, IDependencyNode[]>
    ): Map2<CalculationTarget, ICalculationFunction[]> {
        const cachedNextFunctions = new Map2<CalculationTarget, ICalculationFunction[]>();
        for (const dependencyKey of dependencyMap.keys()) {
            cachedNextFunctions?.set(
                dependencyKey,
                Iterator.from(this.getOrderedFunctionsByGraph(dependencyKey, dependencyMap)).toArray()
            );
        }
        return cachedNextFunctions;
    }

    public ensurePrepared(): void {
        if (this.cachedNextFunctions == undefined) {
            const dependencyMap = this.buildFunctionsGraphAndGetDependencyMap(this.calculationFunctions);
            this.cachedNextFunctions = this.buildCachedOrderedFunctions(dependencyMap);
        }
    }

    private getOrderedFunctions(updatedTarget: CalculationTarget): Iterable<ICalculationFunction> {
        this.ensurePrepared();
        const legacyMask = this.dataDeclarationHelper.getMask(updatedTarget.modelPath);
        const targetPathMask =
            legacyMask != undefined
                ? createFromMask(legacyMask, true, PathTokens.each)
                : this.getMaskDirty(updatedTarget.modelPath);
        const maskTarget = new CalculationTarget(targetPathMask, updatedTarget.targetAttribute);
        const allFinalFunction = this.cachedNextFunctions?.getOrSet(maskTarget, () => []) ?? [];
        return allFinalFunction;
    }

    private getMaskDirty(exactPath: AbsoluteModelPath): AbsoluteModelPath {
        return createAbsoluteFromTokens(
            Iterator.from(exactPath.getPathParts())
                .map(x => (typeof x === "string" && !isNaN(parseInt(x, 10)) ? PathTokens.each : x))
                .toArray()
        );
    }

    private getOrderedFunctionsByGraph(
        maskTarget: CalculationTarget,
        depMap: Map2<CalculationTarget, IDependencyNode[]>
    ): Iterable<ICalculationFunction> {
        const firstFunctions = depMap.get(maskTarget) ?? [];
        const visitedNodes = new Set<number>();
        const filteredNodes: IDependencyNode[] = [];
        for (const node of this.getNextFunctions(...firstFunctions)) {
            if (!visitedNodes.has(node.id)) {
                filteredNodes.push(node);
                visitedNodes.add(node.id);
            }
        }
        const allNodes = reverseArray(filteredNodes);
        return allNodes.map(x => x.calculationFunction);
    }

    private *getNextFunctions(...nodes: IDependencyNode[]): Iterable<IDependencyNode> {
        for (const node of nodes) {
            for (const nextNode of node.nextNodes) {
                if (!nextNode.calculationFunction.getTarget().isEquals(node.calculationFunction.getTarget())) {
                    yield* this.getNextFunctions(nextNode);
                }
            }
            yield node;
        }
    }

    private getAllOrderedFunctions(): ICalculationFunction[] {
        const orderedFns: ICalculationFunction[] = [];
        const visited: CalculationTarget[] = [];
        for (const calculationFunction of this.calculationFunctions) {
            this.dfs(calculationFunction.getTarget(), visited, orderedFns, calculationFunction);
        }
        return orderedFns;
    }

    private dropCache() {
        this.cachedNextFunctions = undefined;
    }

    public getArgumentsPaths(calcPath: AbsoluteModelPath): AbsoluteModelPath[] {
        const calcPathMask = convertToAbsoluteModelPath(this.dataDeclarationHelper.getMask(calcPath) ?? calcPath);

        const result =
            this.calculationFunctions
                .find(fn => {
                    const calcFuncTarget = fn.getTarget();
                    return (
                        calcFuncTarget.targetAttribute === CalculationTargetAttribute.Value &&
                        calcFuncTarget.modelPath.isEquals(calcPathMask)
                    );
                })
                ?.getDependencies()
                ?.map(dep => dep.getDependencyPath())
                ?.map(dep => this.getUnwrappedAggregationTargets(dep))
                ?.flat()
                ?.map(mask => {
                    const dependsOnPath = mask.applyCommonInstancesFromAnotherPath(calcPath);
                    return dependsOnPath;
                })
                ?.flat() ?? [];
        return [...AbsoluteModelPathSet.unique(result)];
    }

    private getUnwrappedAggregationTargets(aggregationModelPath: AbsoluteModelPath): AbsoluteModelPath[] {
        if (isAggregationPath(aggregationModelPath)) {
            return (
                this.calculationFunctions
                    .find(fn => {
                        const calcFuncTarget = fn.getTarget();
                        return (
                            calcFuncTarget.targetAttribute === CalculationTargetAttribute.Value &&
                            calcFuncTarget.modelPath.isEquals(aggregationModelPath)
                        );
                    })
                    ?.getDependencies()
                    ?.map(dep => dep.getDependencyPath())
                    ?.map(dep => this.getUnwrappedAggregationTargets(dep))
                    ?.flat() ?? []
            );
        }
        return [aggregationModelPath];
    }

    private createEvaluationContext(model: IModel, relatedEvents?: Iterable<ModelEvent>) {
        return new EvaluationContext(
            model,
            this.errorManager,
            relatedEvents ?? [],
            this.ignoredPathChecker,
            this.picklistsProvider,
            this.externalPropertiesProvider
        );
    }
}
